import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BuyerParticipationRequestDto, CandidateLimitsDto } from "../../../../autogen/bff-api";
import { useUpdateParticipationRequestSelectionDraftMutation } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { SaveStatusBadge } from "./detail/SaveStatusBadge";
import type { NotesFieldStatus } from "./detail/useBuyerReviewNotesEditor";

const REASON_DEBOUNCE_MS = 400;

export interface CandidateSelection {
  selectedIds: string[];
  notSelected: { requestId: string; reason: string }[];
  isValid: boolean;
  overMax: boolean;
  underFloor: boolean;
  missingReasonCount: number;
  floor: number;
  max: number | null;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  reasonFor: (id: string) => string;
  setReason: (id: string, reason: string) => void;
  applyReasonToAll: (reason: string) => void;
  statusFor: (id: string) => NotesFieldStatus;
  editedByNameFor: (id: string) => string | null;
}

interface PerRequestState {
  deselected: boolean;
  reason: string;
  status: NotesFieldStatus;
}

// Selection state for nedvalg (§ 16-12): backed by per-request selectionDraft on the server.
// Optimistic local state is seeded from server drafts; the keystroke-wipe guard prevents
// server refetches from overwriting values with in-flight saves.
export const useCandidateSelection = (
  eventId: string,
  qualified: BuyerParticipationRequestDto[],
  limits: CandidateLimitsDto | null,
): CandidateSelection => {
  const { trigger: triggerApiError } = useApiError();
  const [updateDraft] = useUpdateParticipationRequestSelectionDraftMutation();

  // Per-request UI state (deselected + reason + save status).
  const [perRequest, setPerRequest] = useState<Record<string, PerRequestState>>(() =>
    Object.fromEntries(
      qualified.map((r) => [
        r.id,
        {
          deselected: r.selectionDraft?.deselected ?? false,
          reason: r.selectionDraft?.reason ?? "",
          status: "idle" as NotesFieldStatus,
        },
      ]),
    ),
  );

  // Pending reason values per request (not yet flushed to server); guards keystroke-wipe.
  const pendingReasons = useRef<Record<string, string>>({});
  // In-flight reason saves per request.
  const inFlightReasons = useRef<Record<string, Promise<void>>>({});
  // Active debounce timers for reason per request.
  const reasonTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Unresolved toggle PATCHes per request (count, to survive rapid double-toggles).
  const inFlightToggles = useRef<Record<string, number>>({});
  // Authoritative deselected value per request — updated synchronously on every toggle call
  // and on hydration, so toggle always reads the latest value regardless of React's render
  // scheduling (functional updaters run during the render phase, not synchronously).
  const deselectedRef = useRef<Record<string, boolean>>(
    Object.fromEntries(qualified.map((r) => [r.id, r.selectionDraft?.deselected ?? false])),
  );
  // Last server-confirmed deselected value per request — used to guard hydration against
  // out-of-order stale refetches: we only update local state when the server sends a genuinely
  // new value, not on every refetch.
  const lastServerDeselected = useRef<Record<string, boolean>>({});
  // Mounted guard: prevents state updates from flush PATCHes that resolve after unmount.
  const isMounted = useRef(true);

  // Hydrate from server data, per field (skip fields with pending/in-flight saves).
  // `qualified` gets a new identity on every refetch, so the reason only syncs when the server
  // value genuinely diverges from local: a matching value keeps the local status ("saved"
  // survives the post-save refetch), and an "error" status keeps the typed-but-unsaved value
  // instead of silently reverting it to the stale server value.
  useEffect(() => {
    setPerRequest((prev) => {
      const qualifiedIds = new Set(qualified.map((r) => r.id));

      // Prune entries for requests that left the qualified set (verdict cleared → SUBMITTED).
      const next: Record<string, PerRequestState> = {};
      for (const id of Object.keys(prev)) {
        if (qualifiedIds.has(id)) {
          next[id] = prev[id];
        } else {
          // Clean up refs for the departing request.
          if (reasonTimers.current[id]) {
            clearTimeout(reasonTimers.current[id]);
            delete reasonTimers.current[id];
          }
          delete pendingReasons.current[id];
          delete inFlightReasons.current[id];
          delete inFlightToggles.current[id];
          delete lastServerDeselected.current[id];
          delete deselectedRef.current[id];
        }
      }

      for (const r of qualified) {
        const reasonGuarded =
          pendingReasons.current[r.id] !== undefined ||
          inFlightReasons.current[r.id] !== undefined;
        const toggleGuarded = (inFlightToggles.current[r.id] ?? 0) > 0;
        const current = next[r.id];
        const serverReason = r.selectionDraft?.reason ?? "";
        const serverDeselected = r.selectionDraft?.deselected ?? false;
        const keepLocalReason =
          reasonGuarded ||
          current?.status === "error" ||
          serverReason === (current?.reason ?? "");

        // Only re-hydrate deselected when:
        // 1. No toggle is in flight (the in-flight count guard), AND
        // 2. The server value genuinely changed from the last value we saw from the server
        //    (value-divergence guard — prevents a stale refetch from reverting a confirmed
        //    optimistic toggle whose PATCH already resolved).
        const prevServerDeselected = lastServerDeselected.current[r.id];
        const serverDeselectedChanged = prevServerDeselected !== serverDeselected;
        const keepLocalDeselected = toggleGuarded || !serverDeselectedChanged;

        lastServerDeselected.current[r.id] = serverDeselected;

        const newDeselected = keepLocalDeselected
          ? (current?.deselected ?? false)
          : serverDeselected;

        // Keep deselectedRef in sync so toggle always reads the latest value.
        if (!keepLocalDeselected) {
          deselectedRef.current[r.id] = newDeselected;
        }

        next[r.id] = {
          deselected: newDeselected,
          reason: keepLocalReason ? (current?.reason ?? "") : serverReason,
          status: keepLocalReason ? (current?.status ?? "idle") : "idle",
        };
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualified]);

  // saveReasonRef: indirection to avoid stale-closure issues in the debounce callback.
  const saveReasonRef = useRef<(id: string) => Promise<void>>(() => Promise.resolve());

  const saveReason = useCallback(
    (id: string): Promise<void> => {
      const existing = inFlightReasons.current[id];
      if (existing) {
        return existing.then(() => saveReasonRef.current(id));
      }

      const value = pendingReasons.current[id];
      if (value === undefined) return Promise.resolve();
      delete pendingReasons.current[id];

      if (isMounted.current) {
        setPerRequest((prev) => ({
          ...prev,
          [id]: { ...prev[id], status: "saving" },
        }));
      }

      const promise: Promise<void> = updateDraft({
        eventId,
        requestId: id,
        selectionDraftPatchDto: { reason: value },
      })
        .unwrap()
        .then(() => {
          if (isMounted.current) {
            setPerRequest((prev) =>
              prev[id]?.status === "saving"
                ? { ...prev, [id]: { ...prev[id], status: "saved" } }
                : prev,
            );
          }
        })
        .catch((e: unknown) => {
          if (isMounted.current) {
            setPerRequest((prev) =>
              prev[id]?.status === "saving"
                ? { ...prev, [id]: { ...prev[id], status: "error" } }
                : prev,
            );
          }
          triggerApiError(e);
        })
        .finally(() => {
          delete inFlightReasons.current[id];
          if (pendingReasons.current[id] !== undefined) {
            void saveReasonRef.current(id);
          }
        });

      inFlightReasons.current[id] = promise;
      return promise;
    },
    [eventId, triggerApiError, updateDraft],
  );
  saveReasonRef.current = saveReason;

  // Flush all pending reason saves on unmount; guard state setters against post-unmount calls.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      for (const [id, timer] of Object.entries(reasonTimers.current)) {
        clearTimeout(timer);
        delete reasonTimers.current[id];
      }
      for (const id of Object.keys(pendingReasons.current)) {
        void saveReasonRef.current(id);
      }
    };
  }, []);

  // toggle: read from deselectedRef (not the React state) so rapid double-clicks each see
  // the most-recent value rather than a stale closure — the ref is updated synchronously
  // before calling setPerRequest.
  const toggle = useCallback(
    (id: string) => {
      // Compute and record the new value synchronously via the ref.
      const newDeselected = !(deselectedRef.current[id] ?? false);
      deselectedRef.current[id] = newDeselected;

      setPerRequest((prev) => ({
        ...prev,
        [id]: { ...prev[id], deselected: newDeselected },
      }));

      inFlightToggles.current[id] = (inFlightToggles.current[id] ?? 0) + 1;

      updateDraft({
        eventId,
        requestId: id,
        selectionDraftPatchDto: { deselected: newDeselected },
      })
        .unwrap()
        .catch((e: unknown) => {
          // Revert optimistic flip on error.
          deselectedRef.current[id] = !newDeselected;
          if (isMounted.current) {
            setPerRequest((prev) => ({
              ...prev,
              [id]: { ...prev[id], deselected: !newDeselected },
            }));
          }
          triggerApiError(e);
        })
        .finally(() => {
          const remaining = (inFlightToggles.current[id] ?? 1) - 1;
          if (remaining <= 0) delete inFlightToggles.current[id];
          else inFlightToggles.current[id] = remaining;
        });
    },
    [eventId, triggerApiError, updateDraft],
  );

  const setReason = useCallback(
    (id: string, reason: string) => {
      setPerRequest((prev) => ({
        ...prev,
        [id]: { ...prev[id], reason, status: "pending" },
      }));
      pendingReasons.current[id] = reason;
      if (reasonTimers.current[id]) clearTimeout(reasonTimers.current[id]);
      reasonTimers.current[id] = setTimeout(() => {
        delete reasonTimers.current[id];
        void saveReasonRef.current(id);
      }, REASON_DEBOUNCE_MS);
    },
    [],
  );

  const applyReasonToAll = useCallback(
    (reason: string) => {
      const deselectedIds = qualified
        .map((r) => r.id)
        .filter((id) => perRequest[id]?.deselected ?? false);

      // Set pending values before firing saves — the setState callback runs lazily during render,
      // but saveReason reads pendingReasons.current synchronously.
      for (const id of deselectedIds) {
        pendingReasons.current[id] = reason;
        if (reasonTimers.current[id]) clearTimeout(reasonTimers.current[id]);
        delete reasonTimers.current[id];
      }

      setPerRequest((prev) => {
        const next = { ...prev };
        for (const id of deselectedIds) {
          next[id] = { ...next[id], reason, status: "pending" };
        }
        return next;
      });

      for (const id of deselectedIds) {
        void saveReasonRef.current(id);
      }
    },
    [qualified, perRequest],
  );

  const qualifiedIds = qualified.map((r) => r.id);
  const selectedIds = qualifiedIds.filter((id) => !(perRequest[id]?.deselected ?? false));
  const floor = limits ? Math.min(limits.minCandidates, qualifiedIds.length) : qualifiedIds.length;
  const max = limits?.maxCandidates ?? null;
  const notSelected = qualifiedIds
    .filter((id) => perRequest[id]?.deselected ?? false)
    .map((id) => ({ requestId: id, reason: (perRequest[id]?.reason ?? "").trim() }));

  const overMax = limits != null && max != null && selectedIds.length > max;
  const underFloor = limits != null && selectedIds.length < floor;
  const missingReasonCount = notSelected.filter((e) => !e.reason).length;
  const isValid = limits == null || (!overMax && !underFloor && missingReasonCount === 0);

  return {
    selectedIds,
    notSelected,
    isValid,
    overMax,
    underFloor,
    missingReasonCount,
    floor,
    max,
    isSelected: (id) => !(perRequest[id]?.deselected ?? false),
    toggle,
    reasonFor: (id) => perRequest[id]?.reason ?? "",
    setReason,
    applyReasonToAll,
    statusFor: (id) => perRequest[id]?.status ?? "idle",
    editedByNameFor: (id) => {
      const r = qualified.find((q) => q.id === id);
      return r?.selectionDraft?.editedByName ?? null;
    },
  };
};

interface Props {
  qualified: BuyerParticipationRequestDto[];
  limits: CandidateLimitsDto;
  selection: CandidateSelection;
}

export const SelectionPanel = ({ qualified, limits, selection }: Props) => {
  const { t } = useTranslation();
  const firstReason = selection.notSelected.find((entry) => entry.reason.length > 0)?.reason;

  const counterText = [
    t("Inviting {{selected}} of {{qualified}} qualified", {
      selected: selection.selectedIds.length,
      qualified: qualified.length,
    }),
    limits.minCandidates != null ? t("min {{min}}", { min: selection.floor }) : null,
    selection.max != null ? t("max {{max}}", { max: selection.max }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box mt="5" borderWidth="1px" borderColor="smBorder" rounded="lg" p="4" bg="smBackground">
      <Flex justify="space-between" align="center" gap="3" wrap="wrap" mb="1">
        {/* tabIndex={-1}: focus target for the qualification stepper's scroll-to-section */}
        <Heading as="h3" fontWeight="semibold" fontSize="sm" color="smText" tabIndex={-1}>
          {t("Selection of participants (announced in the notice)")}
        </Heading>
        <Text fontSize="sm" color="smText">
          {counterText}
        </Text>
      </Flex>
      {limits.selectionCriteria && (
        <Text fontSize="xs" color="smMuted" mb="3">
          {t("Announced criteria")}: {limits.selectionCriteria}
        </Text>
      )}

      <Stack spacing="2">
        {qualified.map((request) => {
          const selected = selection.isSelected(request.id);
          const reason = selection.reasonFor(request.id);
          const reasonBlank = reason.trim().length === 0;
          const status = selection.statusFor(request.id);
          const editedBy = selection.editedByNameFor(request.id);
          return (
            <Box
              key={request.id}
              borderWidth="1px"
              borderColor={!selected && reasonBlank ? "smStatusIssueAccent" : "smBorder"}
              rounded="md"
              p="3"
              bg={!selected && reasonBlank ? "smStatusIssueBg" : "smBackground"}
            >
              <Flex justify="space-between" align="center" gap="2">
                <Checkbox
                  isChecked={selected}
                  onChange={() => selection.toggle(request.id)}
                  colorScheme="teal"
                >
                  <Text as="span" fontWeight="medium" fontSize="sm">
                    {request.organizationName ?? request.organizationId}
                  </Text>
                  {request.organizationNumber && (
                    <Text as="span" fontSize="xs" color="smMuted" ml="2" fontFamily="mono">
                      {request.organizationNumber}
                    </Text>
                  )}
                </Checkbox>
                {selected ? (
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    color="smChipTealFg"
                    bg="smChipTealBg"
                    px="2"
                    py="0.5"
                    rounded="full"
                    flexShrink={0}
                  >
                    {t("Will be invited")}
                  </Text>
                ) : (
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    color="smChipAmberFg"
                    bg="smChipAmberBg"
                    px="2"
                    py="0.5"
                    rounded="full"
                    flexShrink={0}
                  >
                    {t("Not invited")}
                  </Text>
                )}
              </Flex>
              {!selected && (
                <Box mt="2">
                  <Flex align="center" gap="2" mb="1" flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="medium" color="smText">
                      {t("Reason shared with the supplier")}
                      <Box as="span" color="smError" ml="0.5">*</Box>
                    </Text>
                    <SaveStatusBadge status={status} />
                  </Flex>
                  <Textarea
                    size="sm"
                    rounded="md"
                    bg="smBackgroundSecondary"
                    value={reason}
                    onChange={(e) => selection.setReason(request.id, e.target.value)}
                  />
                  <Text fontSize="xs" color="smMuted" mt="1">
                    {t("Sent in writing when the invitation is confirmed (FOA § 16-12 (4)).")}
                  </Text>
                  {editedBy && (
                    <Text fontSize="xs" color="smMuted" mt="0.5">
                      {t("Last edited by {{name}}", { name: editedBy })}
                    </Text>
                  )}
                  {firstReason && reasonBlank && (
                    <Button
                      size="xs"
                      variant="link"
                      colorScheme="teal"
                      mt="1"
                      onClick={() => selection.applyReasonToAll(firstReason)}
                    >
                      {t("Use the same reason for all")}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      {(selection.underFloor || selection.overMax) && (
        <Text mt="3" fontSize="sm" color="smError">
          {selection.underFloor
            ? t("At least {{count}} suppliers must be selected.", { count: selection.floor })
            : t("At most {{count}} suppliers can be selected.", { count: selection.max ?? 0 })}
        </Text>
      )}
    </Box>
  );
};
